//src/mis/components/TableUpload.jsx
import React, { useState, useEffect, useRef } from 'react';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import { Button, Box, Grid, Typography, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, InputAdornment } from '@mui/material';
import EditModal from '../pages/ComplaintsEdit'; // Import the modal
import env from "react-dotenv";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from 'date-fns';
import { FileUpload, UploadFile, Edit, Search, Refresh } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import ExcelJS from 'exceljs'; // Ensure you have this import
import { saveAs } from 'file-saver'; // Ensure you have this import
import * as xlsx from 'xlsx';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import moment from 'moment';
import { normalizeItemKey } from '../utils/misProductColumns';

function buildProductNameLookup(names) {
  const keys = new Set();
  const canonicalByKey = new Map();
  (names || []).forEach((name) => {
    const key = normalizeItemKey(name);
    if (!key) return;
    keys.add(key);
    if (!canonicalByKey.has(key)) canonicalByKey.set(key, String(name).trim());
  });
  return { keys, canonicalByKey };
}

function isKnownProduct(item, lookup) {
  if (!lookup?.keys?.size) return false;
  return lookup.keys.has(normalizeItemKey(item));
}

function resolveCanonicalProductName(item, lookup) {
  const key = normalizeItemKey(item);
  if (!key || !lookup?.canonicalByKey?.has(key)) return null;
  return lookup.canonicalByKey.get(key);
}

function resolveComplaintSheetLayout(sheetData) {
  for (let rowIndex = 0; rowIndex < Math.min(sheetData.length, 8); rowIndex += 1) {
    const row = sheetData[rowIndex] || [];
    const cols = {};
    row.forEach((cell, colIndex) => {
      const header = normalizeItemKey(cell);
      if (!header) return;
      if (header.includes('WRA NO') || header.includes('ADVICE NO')) cols.adviceNo = colIndex;
      else if (
        header.includes('ITEM DESCRIPTION')
        || header === 'ITEM'
        || (header.includes('ITEM') && !header.includes('LIFE OF'))
      ) cols.item = colIndex;
      else if (header.includes('MANUFACTURING')) cols.mfgDate = colIndex;
    });
    if (cols.adviceNo != null && cols.item != null && cols.mfgDate != null) {
      return { cols, dataStartRow: rowIndex + 1 };
    }
  }

  const headerRow = sheetData[2] || sheetData[1] || [];
  const headerText = headerRow.map((h) => String(h || '').toLowerCase()).join('|');
  const isNewFormat = headerText.includes('recovery effective');
  return {
    cols: {
      adviceNo: 1,
      item: isNewFormat ? 6 : 5,
      mfgDate: isNewFormat ? 9 : 8,
    },
    dataStartRow: 3,
  };
}

function parseManufacturingDate(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') {
    return moment(new Date((raw - 25569) * 86400 * 1000)).format('YYYY-MM-DD');
  }
  const text = String(raw).trim();
  const formats = ['MMM-YY', 'MMM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY'];
  for (const fmt of formats) {
    const parsed = moment(text, fmt, true);
    if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
  }
  const loose = moment(text);
  return loose.isValid() ? loose.format('YYYY-MM-DD') : null;
}

function CustomToolbar({ fileName, handleFileChange, handleFileUpload, loading, isUploading,
    selectedCellParams,  onEditClick , handleDialogClose, 
    startDate, setStartDate, endDate, setEndDate, 
    selectedComplaintFrom, setSelectedComplaintFrom, handleSearch,
    selectedItem, setSelectedItem, data, handleExport, productNames, showAdvancedFilters, setShowAdvancedFilters, clearFilters}) {

     const user = useSelector((state) => state.user);
    //  const [isAdmin, setIsAdmin] = useState(user?.role === 'Supply Admin' ? true : false);
    const [isAdmin, setIsAdmin] = useState(() => {
        if (!user || !user.department) return false;
        const role = user.role?.toLowerCase();
        const department = user.department?.toLowerCase();
        return (
          (role === 'admin' && department.includes('supply')) || 
          (role === 'super admin' && department.includes('supply'))
        );
      });
     const [open, setOpen] = useState(false);
     const [openEdit, setOpenEdit] = useState(false);
     const [showUploadFields, setShowUploadFields] = useState(false); // State to manage visibility of upload fields
    
     const handleUploadToggle = () => {
        setShowUploadFields(prev => !prev); // Toggle visibility
    };

  return (
    <GridToolbarContainer>
      <Box sx={{ flexGrow: 1, padding: '16px', display: 'flex', justifyContent: 'space-between' }}>
        
        <Grid container alignItems="center" spacing={2}>
          
          <Grid item>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            onChange={(e) => handleSearch(e.target.value)} // Call the search function
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton>
                    <Search />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            // style={{ width: "100%" }}
              sx={{ 
                    width: '850px', // Set the desired width here
                    backgroundColor: '#ffffff',
                    '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                    borderColor: '#00308F',
                    },
                    '&:hover fieldset': {
                    borderColor: '#00207E',
                    },
                  },
                }}
          />
        </Grid>

        <Grid item>
            <Button  component="label" onClick={handleUploadToggle}>
              {showUploadFields ? 'Upload' : 'Upload'}
            </Button>
          </Grid>

          <Grid item>
                        <Button 
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} // Toggle advanced filters
                            startIcon={<FilterListIcon />}
                        >
                            Filter
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button 
                          onClick={handleExport} 
                          startIcon={<SaveAltIcon />} // Add the icon here
                        >
                          Export
            </Button>
          </Grid>
          
          <Grid item>
          <IconButton
            onClick={() => window.location.reload()} // Refresh the page
            color="primary"
            sx={{
              backgroundColor: '#ffffff',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <Refresh />
          </IconButton>
          </Grid>
      </Grid>
       
        {/* <Button
          variant="contained"
          color="primary"
          onClick={onEditClick} // Trigger edit modal opening
          disabled={!selectedCellParams}
        >
          Edit
        </Button> */}
        {isAdmin && 
        (  
          <>
           <Button color="primary" startIcon={<EditIcon />}
          onClick={()=>{setOpenEdit(true)}}
          disabled={!selectedCellParams}
        >
          Edit
        </Button>
          <EditModal rowData={selectedCellParams} open={openEdit} onClose={() => setOpenEdit(false)}/> 
          </>
            
         )
        }
      </Box>

      {showUploadFields && (
            <>
          <Grid item>
            {/* File input with label */}
            <Button startIcon={<FileUpload />} component="label">
              Choose File
              <input
                type="file"
                hidden
                onChange={handleFileChange} // Handle file selection
              />
            </Button>
          </Grid>

          <Grid item>
            {/* Display selected file name */}
            <Typography variant="body1">
              {fileName ? `Selected File: ${fileName}` : 'No file selected'}
            </Typography>
          </Grid>

          <Grid item>
            {/* Upload button with upload icon */}
            <Button
            
              onClick={handleFileUpload}
              startIcon={<UploadFile />}
              disabled={loading || isUploading || !fileName} // Disable if no file is selected or uploading
            >
              Upload
            </Button>
          </Grid>

          </>
         )}

      {showAdvancedFilters && (
                      <>
          <Grid item>
                        <TextField
                            label="From Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            label="To Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 135 }}>
                            <InputLabel>Complaint From</InputLabel>
                            <Select
                                multiple
                                value={selectedComplaintFrom}
                                onChange={(e) => setSelectedComplaintFrom(e.target.value)}
                                label="Complaint From"
                            >
                                {[...new Set(data.map(item => item.warranty_complaint_from))].map((complaintFrom) => (
                                    <MenuItem key={complaintFrom} value={complaintFrom}>
                                        {complaintFrom}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 70 }}>
                            <InputLabel>Item</InputLabel>
                            <Select
                                multiple
                                value={selectedItem}
                                onChange={(e) => setSelectedItem(e.target.value)}
                                label="Item"
                            >
                                {productNames.map((item) => ( // Use productNames for dropdown options
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    {/* Clear All Filters Button */}
                    <Grid item>
                      <Button
                        onClick={clearFilters}
                        // startIcon={<ClearIcon />} // Add the clear icon here
                      >
                        Clear Filter
                      </Button>
                    </Grid>

                    </>
            )}

    </GridToolbarContainer>
  );
}

function WarrantyComplaints() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const uploadLockRef = useRef(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(''); // State for displaying the file name
  const navigate = useNavigate();
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedComplaintFrom, setSelectedComplaintFrom] = useState([]); // Initialize as an array
  const [selectedItem, setSelectedItem] = useState([]); // Initialize as an array
  const [filteredData, setFilteredData] = useState([]);
  const [productNames, setProductNames] = useState([]); // State for product names
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false); // State for advanced filters
  const [searchQuery, setSearchQuery] = useState('');

  // console.log("Fetch Data:- ", data);
  
  const location = useLocation();
  useEffect( () => {
    
    if(location.key === undefined || location.key === null || location.key === 'default'){
      
      navigate("/");   
    }
  }, [ location ] );

  const columns = [
    { field: 'id', headerName: 'ID', width: 40 },
    { field: 'warranty_rejection_advice_no', headerName: 'Advice No.', width: 120 },
    { field: 'warranty_rejection_advice_date', headerName: 'Advice Date', width: 90 },
    { field: 'recovery_effective_date', headerName: 'Recovery Effective Date', width: 120 },
    { field: 'warranty_complaint_from', headerName: 'Complaint From', width: 180 },
    { field: 'rly', headerName: 'Rly', width: 45 },
    { field: 'item', headerName: 'Item', width: 95 },
    { field: 'qty_rejected_under_warranty', headerName: 'Qty Rejected', width: 65 },
    { field: 'inspection_by', headerName: 'Inspection By', width: 75 },
    { field: 'manufacturing_date', headerName: 'Manufacturing Date', width: 100 },
    { field: 'life_of_item', headerName: 'Life of Item', width: 60 },
    { field: 'original_claim_value', headerName: 'Original Claim Value', width: 95 },
    { field: 'reason_of_rejection', headerName: 'Reason Of Rejection', width: 130 },
  ];

  useEffect(() => {
    // Fetch data when component loads
    fetchData();
    fetchProductNames(); // Fetch product names on component mount
  }, []);

  const fetchData = async () => {
    
    setLoading(true);
    try {
     
      const response = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/complaints`);
      const result = await response.json();

      const processedData = result.map((row, index) => ({
        id: index + 1,
        ...row,
        warranty_rejection_advice_date: row.warranty_rejection_advice_date && format(new Date(row.warranty_rejection_advice_date), 'yyyy-MM-dd'),
        recovery_effective_date: row.recovery_effective_date && format(new Date(row.recovery_effective_date), 'yyyy-MM-dd'),
        manufacturing_date : row.manufacturing_date && format(new Date(row.manufacturing_date), 'MMM-yy'),
      }));
    
      setData(processedData);
      setFilteredData(processedData); // Initialize filtered data
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductNames = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`);
        const result = await response.json();
        const names = result
          .map((product) => product.product_name)
          .filter(Boolean)
          .map((name) => String(name).trim());
        setProductNames(names);
    } catch (error) {
        console.error('Error fetching product names:', error);
    }
};

  //Filter Data
  useEffect(() => {
    const filtered = data.filter((row) => {
        const rowDate = new Date(row.warranty_rejection_advice_date); // Convert row date to Date object
        const start = startDate ? new Date(startDate) : null; // Convert startDate to Date object if set
        const end = endDate ? new Date(endDate) : null; // Convert endDate to Date object if set

        const matchesDate = 
            (!start && !end) || // If neither date is set, include all
            (start && !end && rowDate.toDateString() === start.toDateString()) || // Only From Date is set
            (!start && end && rowDate <= end) || // Only To Date is set
            (start && end && rowDate >= start && rowDate <= end); // Both dates are set

        const matchesComplaintFrom = selectedComplaintFrom.length === 0 || selectedComplaintFrom.includes(row.warranty_complaint_from);
        const matchesItem = selectedItem.length === 0 || selectedItem.includes(row.item);

        const matchesSearch = Object.values(row).some((field) =>
          field && field.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );

        return matchesDate && matchesComplaintFrom && matchesItem && matchesSearch;
    });

    // console.log("Filtered Data:", filtered); // Log filtered data for debugging
    setFilteredData(filtered);
}, [data, startDate, endDate, selectedComplaintFrom, selectedItem, searchQuery, showAdvancedFilters]);  // Add showAdvancedFilters to dependencies

//Export Data
const formatExportDate = (value, useDashSeparator = false) => {
  if (!value) return '';
  const parsed = moment(value, ['yyyy-MM-dd', 'YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid()
    ? parsed.format(useDashSeparator ? 'DD-MM-YYYY' : 'DD/MM/YYYY')
    : value;
};

const handleExport = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Warranty Complaints');
  const lastCol = 'M';

  // Add header rows
  const titleRow = worksheet.addRow(['ARYAN EXPORTERS PVT. LTD.']); // Change to your company name
  worksheet.mergeCells(`A1:${lastCol}1`); // Adjust the range based on the number of columns
  titleRow.alignment = { horizontal: 'center', vertical: 'middle' }; // Center alignment
  const mergedTitleCell = worksheet.getCell('A1');
  mergedTitleCell.font = { bold: true, size: 14 }; // Set font for the merged cell

  const summaryRow = worksheet.addRow([`Summary Of Warranty Complaints ${startDate && endDate ? `from ${startDate} to ${endDate}` : startDate ? `on ${startDate}` : 'for all dates'}`]);
  worksheet.mergeCells(`A2:${lastCol}2`); // Adjust the range based on the number of columns
  summaryRow.alignment = { horizontal: 'center', vertical: 'middle' }; // Center alignment
  const mergedSummaryCell = worksheet.getCell('A2');
  mergedSummaryCell.font = { bold: true, size: 12 }; // Set font for the merged cell

  // Add headers
  const excelHeaders = [
      'S/N',
      'WRA No.',
      'WRA Date',
      'Recovery Effective Date',
      'Warranty Complaint From',
      'RLY',
      'Item Description',
      'Qty Rejected under Warranty',
      'Inspected BY',
      'Manufacturing Date',
      'Life Of ITEM',
      'Original Claim Value (Rs.)',
      'Reason Of Rejection',
  ];
  worksheet.addRow(excelHeaders).font = { bold: true };

  // Add filtered data
  filteredData.forEach((row, index) => {
      worksheet.addRow([
          index + 1, // S/N
          row.warranty_rejection_advice_no || '',
          formatExportDate(row.warranty_rejection_advice_date),
          formatExportDate(row.recovery_effective_date, true),
          row.warranty_complaint_from || '',
          row.rly || '',
          row.item || '',
          row.qty_rejected_under_warranty || '',
          row.inspection_by || '',
          row.manufacturing_date || '',
          row.life_of_item || '',
          row.original_claim_value || '',
          row.reason_of_rejection || '',
      ]);
  });

  // Apply styles to the worksheet
  worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
          // Apply border to all cells, including empty ones
          cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });

      // Bold for header rows
      if (rowNumber === 3) { // Header row is the third row after title and summary
          row.font = { bold: true };
      }
  });

  // Set column widths
  worksheet.getColumn(1).width = 5;   // S/N
  worksheet.getColumn(2).width = 20; // WRA No.
  worksheet.getColumn(3).width = 16;  // WRA Date
  worksheet.getColumn(4).width = 22;  // Recovery Effective Date
  worksheet.getColumn(5).width = 30;  // Warranty Complaint From
  worksheet.getColumn(6).width = 10;  // RLY
  worksheet.getColumn(7).width = 18;  // Item Description
  worksheet.getColumn(8).width = 18;  // Qty Rejected under Warranty
  worksheet.getColumn(9).width = 15;  // Inspected BY
  worksheet.getColumn(10).width = 18; // Manufacturing Date
  worksheet.getColumn(11).width = 14; // Life Of ITEM
  worksheet.getColumn(12).width = 22; // Original Claim Value (Rs.)
  worksheet.getColumn(13).width = 24; // Reason Of Rejection

  // Save the workbook
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  saveAs(blob, 'WarrantyComplaints.xlsx');
  };
  
const clearFilters = () => {
  setStartDate("");
  setEndDate("");
  setSelectedComplaintFrom([]);
  setSelectedItem([]);
  // Reset any other filters if necessary
};

  const handleRowSelection = (params) => {
    setSelectedRow(params);
  };

  const handleDialogOpen = () => {
    if (!selectedRow) {
      alert('Please select a row first!');
      return;
    }
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
  };

  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file); // Set the file state
      setFileName(file.name); // Set the file name for display
    }
  };

  const handleFileUpload = async () => {
  if (isUploading || uploadLockRef.current) return;
  uploadLockRef.current = true;
  setIsUploading(true);
  try {
    await handleFileUploadInner();
  } finally {
    uploadLockRef.current = false;
    setIsUploading(false);
  }
}

//console.log("Product list:- ", productNames);

const handleFileUploadInner = async () => { 
    if (!file) {
      alert("Please choose a file first!");
      return;
    }

  // Use the existing records from the state
  const existingRecords = data; // Use the already fetched data

  // Step 2: Read the new file and validate
  const newRecords = await readExcelFile(file, productNames); // Read the Excel file
  const productLookup = buildProductNameLookup(productNames);
  const duplicates = [];
  const skippedRecords = [];

  // Check if there are any valid records
  if (newRecords.length === 0) {
      alert("The uploaded file does not contain any valid records.");
      return; // Prevent upload
  }

  // Step 3: Check if items exist in the product list
  for (const newRecord of newRecords) {
      const { item, manufacturing_date } = newRecord;

      // Check if the item exists in the productNames array (normalized match)
      if (!isKnownProduct(item, productLookup)) {
          skippedRecords.push(newRecord);
          continue; // Skip this record if item does not exist
      }

      const canonicalItem = resolveCanonicalProductName(item, productLookup) || String(item || '').trim();

      // Check for duplicates
      const { warranty_rejection_advice_no } = newRecord;
      const isDuplicate = existingRecords.some(existingRecord => 
          existingRecord.warranty_rejection_advice_no === warranty_rejection_advice_no &&
          normalizeItemKey(existingRecord.item) === normalizeItemKey(canonicalItem) &&
          existingRecord.manufacturing_date === manufacturing_date // Check for the same manufacturing date
      );

      if (isDuplicate) {
          duplicates.push(newRecord);
          continue; // Continue to the next record if it's a duplicate
      }

      // If not a duplicate, you can process the record for insertion here
      // You can add the record to a list for insertion later
  }

  // Step 4: Handle duplicates
  if (duplicates.length > 0) {
    alert("Duplicate records found so skipped that record:\n" + duplicates.map(record => `Advice No: ${record.warranty_rejection_advice_no}, Item: ${record.item}, Manufacturing Date: ${record.manufacturing_date}`).join('\n'));
      // Instead of returning, just log the duplicates and continue
  }

  // Step 5: Handle skipped records
  if (skippedRecords.length > 0) {
      alert("The following records were skipped because the item does not exist in the product list:\n" + skippedRecords.map(record => `Advice No: ${record.warranty_rejection_advice_no}, Item: ${record.item}`).join('\n'));
      if (skippedRecords.length === newRecords.length) {
        return;
      }
  }

  // Proceed with the upload if no duplicates are found
    const formData = new FormData();
    formData.append('file', file);
  formData.append('productNames', JSON.stringify(productNames)); // Append product names to form data
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/upload`, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (response.ok) {
          navigate("../efficiencyHome/5");
          navigate(0);
      } else {
        alert(result?.message || result?.error || "File upload failed.");
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setLoading(false);
    }
  };

// Function to read the Excel file and return the records
const readExcelFile = async (file, knownProductNames = []) => {
  const data = [];
  const reader = new FileReader();
  const productLookup = buildProductNameLookup(knownProductNames);
  
  return new Promise((resolve, reject) => {
      reader.onload = async (e) => {
          const binaryStr = e.target.result;
          const workbook = xlsx.read(binaryStr, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheetData = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: '' });
          const { cols, dataStartRow } = resolveComplaintSheetLayout(sheetData);
          const dataRows = sheetData.slice(dataStartRow);

          dataRows.forEach(row => {
              const adviceNo = String(row[cols.adviceNo] ?? '').trim();
              if (!adviceNo) return;

              const rawItem = row[cols.item];
              const item = resolveCanonicalProductName(rawItem, productLookup)
                || String(rawItem ?? '').trim().replace(/\s+/g, ' ');
              const manufacturingDate = parseManufacturingDate(row[cols.mfgDate]);

              if (!item) return;

              if (!manufacturingDate) {
                  console.error(`Invalid manufacturing date for record: ${JSON.stringify(row)}`);
                  return;
              }

              data.push({
                  warranty_rejection_advice_no: adviceNo,
                  item,
                  manufacturing_date: manufacturingDate,
              });
          });

          resolve(data);
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
  });
};

  const [selectedCellParams, setSelectedCellParams] = React.useState(null);
  const handleCellFocus = React.useCallback((event) => {
    const row = event.currentTarget.parentElement;
    const dataId = row.dataset.id;   
    const id = row.dataset.rowindex;
    const field = event.currentTarget.dataset.field;
    setSelectedCellParams({ id, field , dataId });
    setSelectedRow(dataId)
  }, []);

  const handleSearch = (query) => {
  setSearchQuery(query);
  const filtered = data.filter((row) => {
    return Object.values(row).some((field) =>
      field && field.toString().toLowerCase().includes(query.toLowerCase())
    );
  });
  setFilteredData(filtered);
};

  return (
    <Box 
      sx={{
        height: 600,
        width: '95%',
        marginLeft:"180px",
        paddingRight:"90px"
      }}
      >
      <DataGrid
      sx={{
        boxShadow: 1,
        border: 1,
        
        borderColor: 'primary.light',
        typography: 'subtitle2',
        '& .MuiDataGrid-cell:hover': {
          color: 'primary.main',
        },
        '.MuiDataGrid-columnHeaderTitle': { 
            font: 'light important',
            overflow: 'visible !important'
         }
      }}
        rows={filteredData} // Use filteredData here
        columns={columns}
        pageSize={10}
        disableMultipleRowSelection
        rowsPerPageOptions={[10, 20, 50]}
        loading={loading}
        checkboxSelection
        //onRowSelectionModelChange={handleRowSelection} // Set the selected row
    
        slots={{
            toolbar: CustomToolbar,
        }}
          slotProps={{
            toolbar: { 
                fileName:fileName,
                handleFileChange:handleFileChange,
                handleFileUpload:handleFileUpload,
                loading:loading,
              isUploading:isUploading,
                selectedCellParams:selectedCellParams,
                onEditClick: handleDialogOpen,
                handleDialogClose:handleDialogClose,
                startDate: startDate,
                setStartDate: setStartDate,
                endDate: endDate,
                setEndDate: setEndDate,
                selectedComplaintFrom: selectedComplaintFrom,
                setSelectedComplaintFrom: setSelectedComplaintFrom,
                selectedItem: selectedItem,
                setSelectedItem: setSelectedItem,
                data: data,
                handleExport: handleExport,
                productNames: productNames, // Pass product names to the toolbar
                showAdvancedFilters: showAdvancedFilters, // Pass the state
                setShowAdvancedFilters: setShowAdvancedFilters, // Pass the setter
                clearFilters:clearFilters,
                handleSearch: handleSearch, // Pass the search function to the toolbar
             },
            cell: {
                onFocus: handleCellFocus,
              },
          }}
        
      />
      {/* {selectedRow && (
        <EditModal
          open={isEditDialogOpen}
          onClose={handleDialogClose}
          rowData={selectedRow} // Pass selected row data to the modal
        />
      )} */}
    </Box>
  );
}

export default WarrantyComplaints;